import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ImagesService } from '@app/_services/images/images.service';
import {
    ClearDeletedImages,
    ClearInstagramPhotos,
    DeleteImage,
    GetFilesDocs,
    GetFilesDocsSize,
    GetImagesList,
    GetInstagram,
    GetInstagramPhotos,
    GetOptimizationConfig,
    GetPexelsData,
    GetResourcesConfig,
    LogoutInstagram,
    ResetUploadedFiles,
    SaveInstagramToken,
    SetFilters,
    SetOptimizationConfig,
    SetPexelsFilter,
    UpdateInstagram,
    UploadPhoto,
} from './images.actions';
import {
    DeletedImage,
    FileItem,
    GeneralData,
    ImageItem,
    ImagesFilter,
    InstagramAccountData,
    InstagramResponse,
    KeepMimeData,
    MapTypeResponse,
    MimeData,
    MODULES_OPTIONS,
    PEXELS_FILTER,
    PEXELS_PHOTO,
    PRIORITY,
    ResourcesDataList,
    ResourcesIdsData,
    SizeStatus,
    UploadedItem,
} from '@app/_models/images-manager/images-management.model';
import { catchError, tap } from 'rxjs';
import { McToastService } from '@app/_services';
import { TranslateService } from '@ngx-translate/core';

export interface ImagesStateModel {
    instagramResponse: InstagramResponse;
    // instagramPhotos: InstagramPhoto[];
    instagramPhotos: string[];
    instagramNext: string;
    filter: ImagesFilter;
    fileFilter: ImagesFilter;
    showEmptyState: boolean;
    imageList: ImageItem[]; // Listado Imagenes
    imageListSize: number;
    fileList: FileItem[]; // Listado archivos
    fileListSize: number;
    uploadedFiles: UploadedItem[];
    deletedImages: DeletedImage[];
    optimizationConfig: any;
    instagramAccountData: InstagramAccountData;
    resourcesData: GeneralData | MimeData | KeepMimeData | ResourcesIdsData | ResourcesDataList | MapTypeResponse[];
    pexelsFilter: PEXELS_FILTER;
    pexelsImages: PEXELS_PHOTO;
    pexelsNext: string;
    showPexelsEmptyState: boolean;
    sizeData: SizeStatus;
}

const initialState: ImagesStateModel = {
    instagramResponse: null,
    instagramPhotos: [],
    filter: {
        search: null,
        start: 1,
        limit: 10,
        order: 'id',
        priority: PRIORITY.DESC,
        module: MODULES_OPTIONS.ALL,
    },
    fileFilter: {
        search: null,
        start: 1,
        limit: 10,
        order: 'id',
        priority: PRIORITY.DESC,
        module: MODULES_OPTIONS.SITE_FILES,
    },
    showEmptyState: false,
    imageList: [],
    imageListSize: 0,
    fileList: [],
    fileListSize: 0,
    instagramNext: null,
    uploadedFiles: [],
    deletedImages: [],
    optimizationConfig: null,
    instagramAccountData: null,
    resourcesData: null,
    pexelsFilter: {
        ep_key: 'curated_photos',
        args: {
            per_page: 20,
            page: 1,
        },
    },
    pexelsImages: null,
    pexelsNext: null,
    showPexelsEmptyState: false,
    sizeData: null,
};

@State<ImagesStateModel>({
    name: 'Images',
    defaults: initialState,
})
@Injectable()
export class ImagesState {
    constructor(
        private toast: McToastService,
        private translate: TranslateService,
        private imageService: ImagesService
    ) {}

    @Selector()
    public static getInstagramConfig(state: ImagesStateModel) {
        return state.instagramResponse;
    }

    @Selector()
    public static getInstagramPhotos(state: ImagesStateModel) {
        return state.instagramPhotos;
    }

    @Selector()
    public static getInstagramNext(state: ImagesStateModel) {
        return state.instagramNext;
    }

    @Selector()
    public static getImageList(state: ImagesStateModel) {
        return state.imageList;
    }

    @Selector()
    public static getImageListSize(state: ImagesStateModel) {
        return state.imageListSize;
    }

    @Selector()
    public static getFileList(state: ImagesStateModel) {
        return state.fileList;
    }

    @Selector()
    public static getFileListSize(state: ImagesStateModel) {
        return state.fileListSize;
    }

    @Selector()
    public static getShowEmptyState(state: ImagesStateModel) {
        return state.showEmptyState;
    }

    @Selector()
    public static getImagesFilter(state: ImagesStateModel) {
        return state.filter;
    }

    @Selector()
    public static getFilesFilter(state: ImagesStateModel) {
        return state.fileFilter;
    }

    @Selector()
    public static getUploadedFiles(state: ImagesStateModel) {
        return state.uploadedFiles;
    }

    @Selector()
    public static getDeletedImages(state: ImagesStateModel) {
        return state.deletedImages;
    }

    @Selector()
    public static getOptimizationConfig(state: ImagesStateModel) {
        return state.optimizationConfig;
    }

    @Selector()
    public static getAccountData(state: ImagesStateModel) {
        return state.instagramAccountData;
    }

    @Selector()
    public static getResourcesData(state: ImagesStateModel) {
        return state.resourcesData;
    }

    @Selector()
    public static getPexelsFilter(state: ImagesStateModel) {
        return state.pexelsFilter;
    }

    @Selector()
    public static getPexelsImages(state: ImagesStateModel) {
        return state.pexelsImages;
    }

    @Selector()
    public static getPexelsNextUrl(state: ImagesStateModel) {
        return state.pexelsNext;
    }

    @Selector()
    public static getShowPexelsEmptyState(state: ImagesStateModel) {
        return state.showPexelsEmptyState;
    }

    @Selector()
    public static getSizeData(state: ImagesStateModel) {
        return state.sizeData;
    }

    @Action(GetInstagram)
    public GetInstagram({ patchState }: StateContext<ImagesStateModel>) {
        return this.imageService.getInstagramConfig().subscribe({
            next: (response) => {
                if (response.success) {
                    const data = {
                        loggedIn: response.data['logged-in'],
                        url: response.data.url,
                    };
                    patchState({
                        instagramResponse: data,
                    });
                }
            },
            error: (error) => {
                throw error;
            },
        });
    }

    @Action(UpdateInstagram)
    public UpdateInstagram({ patchState }: StateContext<ImagesStateModel>, { instagramResponse }: UpdateInstagram) {
        patchState({ instagramResponse });
    }

    @Action(SaveInstagramToken)
    public saveInstagramToken({ patchState, getState }: StateContext<ImagesStateModel>, { token }: SaveInstagramToken) {
        const prevData = getState().instagramResponse;
        return this.imageService.saveInstagramToken(token).pipe(
            tap((response) => {
                if (response.success) {
                    prevData.loggedIn = true;
                    patchState({
                        instagramResponse: prevData,
                    });
                }
            }),
            catchError((error) => {
                this.toast.danger(this.translate.instant('app.global.modals.image_manager.toasts.ig_token_error'));
                throw error;
            })
        );
    }

    @Action(GetInstagramPhotos)
    public GetInstagramPhotos({ patchState, getState }: StateContext<ImagesStateModel>, { next }: GetInstagramPhotos) {
        let prevData = getState().instagramResponse;
        return this.imageService.getInstagramPhotos(next).pipe(
            tap((response) => {
                if (response.success) {
                    if (prevData) {
                        prevData.loggedIn = response.data['logged-in'];
                        prevData.url = response?.data?.url ?? prevData?.url;
                    } else {
                        prevData = {
                            loggedIn: response.data['logged-in'],
                            url: prevData?.url ?? response?.data?.url,
                        };
                    }
                    if (prevData.loggedIn) {
                        patchState({
                            instagramPhotos: response.data.images,
                            instagramNext: response.data.next,
                            instagramAccountData: response.data.username
                                ? {
                                      username: response.data.username,
                                      username_id: response.data.username_id,
                                  }
                                : null,
                            instagramResponse: prevData,
                        });
                    } else {
                        patchState({
                            instagramResponse: prevData,
                        });
                    }
                }
            }),
            catchError((error) => {
                this.toast.danger(this.translate.instant('app.global.modals.image_manager.toasts.ig_images_error'));
                throw error;
            })
        );
    }

    @Action(SetFilters)
    public SetFilters({ patchState }: StateContext<ImagesStateModel>, { filter, isFileUpload }: SetFilters) {
        if (isFileUpload) {
            patchState({ fileFilter: filter });
        } else {
            patchState({ filter });
        }
    }

    @Action(GetImagesList)
    public GetImagesList({ patchState, getState }: StateContext<ImagesStateModel>) {
        const filters = getState().filter;
        for (const key in filters) {
            if (filters[key] === null) delete filters[key];
        }
        return this.imageService.getAllImages(filters).pipe(
            tap((response) => {
                if (response.success) {
                    const images = response.data.items;
                    const totalItems = response.data.total_items;
                    const showEmptyState = filters.search === undefined && (!images?.length || totalItems === 0);
                    patchState({
                        imageList: images,
                        imageListSize: totalItems,
                        showEmptyState,
                    });
                } else {
                    this.toast.danger(this.translate.instant('app.global.toast.no_se_pudo_realizar'));
                }
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    @Action(UploadPhoto)
    public UploadPhoto(
        { patchState }: StateContext<ImagesStateModel>,
        { payload, showToast = true, isFile = false }: UploadPhoto
    ) {
        return this.imageService.uplodaImage(payload).pipe(
            tap((response) => {
                if (response.success) {
                    const { successUploads, errorUploads } = this.separateUploads(response.data);

                    if (showToast) {
                        if (successUploads.length) {
                            this.toast.success(this.getSuccessMessage(isFile));
                        }
                        errorUploads.forEach((file) => {
                            this.toast.danger(this.getErrorMessage(isFile, file.filename));
                        });
                    }

                    patchState({
                        uploadedFiles: successUploads.map((item) => item.data),
                    });
                }
            }),
            catchError((error) => {
                if (showToast) {
                    this.handleUploadError(error, payload, isFile);
                }
                throw error;
            })
        );
    }

    private separateUploads(data: any[]): { successUploads: any[]; errorUploads: any[] } {
        const successUploads: any[] = [];
        const errorUploads: any[] = [];

        data.forEach((item) => {
            if (item.success) {
                successUploads.push(item);
            } else {
                errorUploads.push(item);
            }
        });

        return { successUploads, errorUploads };
    }

    private getSuccessMessage(isFile: boolean): string {
        const key = isFile
            ? 'app.global.modals.image_manager.toasts.file_save_success'
            : 'app.global.modals.image_manager.toasts.img_save_success';
        return this.translate.instant(key);
    }

    private getErrorMessage(isFile: boolean, fileName: string = ''): string {
        const key = isFile
            ? 'app.global.modals.image_manager.toasts.file_save_error_msg'
            : 'app.global.modals.image_manager.toasts.img_save_error_msg';
        const params = isFile ? { file: fileName } : { img: fileName };
        return this.translate.instant(key, params);
    }

    private handleUploadError(error: any, payload: FormData, isFile: boolean): void {
        if (error?.error?.message && Object.keys(error.error.message).length > 0) {
            this.handleValidationErrors(error.error.message, payload, isFile);
        } else if (error?.error?.status === 413 || error?.status === 413) {
            const sizeErrorMsg = isFile
                ? this.translate.instant('El archivo es demasiado grande. El tamaño máximo permitido es de 50MB.')
                : this.translate.instant('La imagen es demasiado grande. El tamaño máximo permitido es de 50MB.');
            this.toast.danger(sizeErrorMsg);
        } else {
            this.toast.danger(this.getErrorMessage(isFile));
        }
    }

    private handleValidationErrors(errorMessages: any, payload: FormData, isFile: boolean): void {
        Object.keys(errorMessages).forEach((errorKey) => {
            const splitedKey = errorKey.split('.');
            const fixedKey = `${splitedKey[0]}[${splitedKey[1]}][${splitedKey[2]}]`;
            const file = payload.get(fixedKey);

            if (file) {
                const fileName = typeof file === 'object' ? file.name : file;
                this.toast.danger(this.getErrorMessage(isFile, fileName ?? ''));
            }
        });
    }

    @Action(LogoutInstagram)
    public LogoutInstagram({ patchState, getState }: StateContext<ImagesStateModel>) {
        const prevData = getState().instagramResponse;
        return this.imageService.instagramLogout().pipe(
            tap((response) => {
                if (response.success) {
                    prevData.loggedIn = false;
                    patchState({
                        instagramResponse: prevData,
                        instagramPhotos: [],
                        instagramNext: null,
                        instagramAccountData: null,
                    });
                    this.toast.success(
                        this.translate.instant('app.global.modals.image_manager.toasts.ig_logout_success')
                    );
                }
            }),
            catchError((error) => {
                this.toast.danger(this.translate.instant('app.global.modals.image_manager.toasts.ig_logout_error'));
                throw error;
            })
        );
    }

    @Action(ResetUploadedFiles)
    public ResetUploadedFiles({ patchState }: StateContext<ImagesStateModel>) {
        patchState({ uploadedFiles: [] });
    }

    @Action(DeleteImage)
    public DeleteImage(
        { patchState, getState }: StateContext<ImagesStateModel>,
        { payload, isFileUpload }: DeleteImage
    ) {
        const prevData = getState().instagramResponse;
        return this.imageService.deleteImages(payload).pipe(
            tap((response) => {
                if (response.success) {
                    const successMessage = isFileUpload
                        ? this.translate.instant('app.global.modals.image_manager.toasts.file_delete_success')
                        : this.translate.instant('app.global.modals.image_manager.toasts.img_delete_success');
                    this.toast.success(successMessage);
                    patchState({ deletedImages: response.data });
                }
            }),
            catchError((error) => {
                const errorMessage = isFileUpload
                    ? this.translate.instant('app.global.modals.image_manager.toasts.file_delete_error')
                    : this.translate.instant('app.global.modals.image_manager.toasts.img_delete_error');
                this.toast.danger(errorMessage);
                throw error;
            })
        );
    }

    @Action(ClearDeletedImages)
    public ClearDeletedImages({ patchState }: StateContext<ImagesStateModel>) {
        patchState({ deletedImages: [] });
    }

    @Action(GetOptimizationConfig)
    public GetOptimizationConfig({ patchState }: StateContext<ImagesStateModel>) {
        return this.imageService.getOptimizationConfig().subscribe({
            next: (response) => {
                if (response?.success) {
                    patchState({
                        optimizationConfig: response?.data?.optimice_images_resources ?? false,
                    });
                }
            },
            error: (error) => {
                throw error;
            },
        });
    }

    @Action(SetOptimizationConfig)
    public SetOptimizationConfig({ patchState }: StateContext<ImagesStateModel>, { status }: SetOptimizationConfig) {
        return this.imageService.setOptimizationConfig(status).subscribe({
            next: (response) => {
                if (response.success) {
                    this.toast.success(
                        this.translate.instant('app.global.modals.image_manager.config.optimization.toast.success')
                    );
                }
            },
            error: (error) => {
                throw error;
            },
        });
    }

    @Action(ClearInstagramPhotos)
    public ClearInstagramPhotos({ patchState, getState }: StateContext<ImagesStateModel>) {
        const prevData = getState().instagramResponse;
        prevData.loggedIn = false;
        patchState({
            instagramPhotos: [],
            instagramNext: null,
            instagramAccountData: null,
            instagramResponse: prevData,
        });
    }

    @Action(GetResourcesConfig)
    public GetResourcesConfig(
        { patchState }: StateContext<ImagesStateModel>,
        { payload, module, relation_id }: GetResourcesConfig
    ) {
        return this.imageService.getResourcesData(payload, module, relation_id).pipe(
            tap((response) => {
                if (response.success) {
                    patchState({ resourcesData: response.data });
                }
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    @Action(SetPexelsFilter)
    public SetPexelsFilters({ patchState }: StateContext<ImagesStateModel>, { filter }: SetPexelsFilter) {
        patchState({ pexelsFilter: filter });
    }

    @Action(GetPexelsData)
    public GetPexelsData({ patchState, getState }: StateContext<ImagesStateModel>) {
        const filters = getState().pexelsFilter;
        for (const key in filters) {
            if (filters[key] === null) delete filters[key];
        }
        return this.imageService.getPexelsData(filters).pipe(
            tap((response) => {
                if (response.success) {
                    const images = response.data?.data?.photos ?? [];
                    const totalItems = response.data?.data?.total_results ?? 0;
                    const showEmptyState = !images?.length || totalItems === 0;
                    patchState({
                        pexelsImages: images,
                        pexelsNext: response.data?.data?.next_page,
                        showPexelsEmptyState: showEmptyState,
                    });
                } else {
                    this.toast.danger(this.translate.instant('app.global.toast.no_se_pudo_realizar'));
                }
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    /**
     * ARCHIVOS
     */
    @Action(GetFilesDocs)
    public GetFilesDocs({ patchState, getState }: StateContext<ImagesStateModel>) {
        const filters = getState().fileFilter;
        for (const key in filters) {
            if (filters[key] === null) delete filters[key];
        }
        return this.imageService.getAllFiles(filters).pipe(
            tap((response) => {
                if (response.success) {
                    const files = response.data.items;
                    const totalItems = response.data.total_items;
                    const showEmptyState = filters.search === undefined && (!files?.length || totalItems === 0);
                    patchState({
                        fileList: files,
                        fileListSize: totalItems,
                        showEmptyState,
                    });
                } else {
                    this.toast.danger(this.translate.instant('app.global.toast.no_se_pudo_realizar'));
                }
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    @Action(GetFilesDocsSize)
    public GetFilesDocsSize({ patchState }: StateContext<ImagesStateModel>) {
        return this.imageService.getFilesSize().pipe(
            tap((response) => {
                if (response.success) {
                    const data: SizeStatus = { ...response.data, usedPercentage: 0, usedMB: 0, maxGB: 0 };
                    patchState({
                        sizeData: data,
                    });
                } else {
                    this.toast.danger(this.translate.instant('app.global.toast.no_se_pudo_realizar'));
                }
            }),
            catchError((error) => {
                throw error;
            })
        );
    }
}
